/**
 * Board-agnostic "add this player to a pipeline stage" mutation (SE-5499).
 *
 * This is the outside-in add path: a player is added to a pipeline board +
 * stage from a surface that has NO open-board context (the player profile
 * today; the Split-PRD selection toolbar later). It deliberately does NOT reuse
 * `useKanbanCardOperations.addPlayerToBoard`, which closes over the currently
 * open board and its cards and short-circuits to a move when a card already
 * exists — none of that context exists on the profile page.
 *
 * Cache strategy — refetch, NOT optimism (matches the established board add
 * path, commits 0a39507a3 / e827de530): the board's card list is read through
 * `ListKanbanCards`, whose node type is `PlayerWithKanban`, while this mutation
 * returns a bare `KanbanCard`. An optimisticResponse can't produce the shape the
 * list cache normalizes, so we let a targeted `refetchQueries: ["ListKanbanCards"]`
 * reconcile the board if it happens to be mounted. Adding an optimisticResponse
 * here would reintroduce the SE-5058 error-code-13 "Missing field" flood.
 *
 * The live contract (Cody, 2026-07-24): an add is ALWAYS a direct add. A
 * sensitive (`isProtected`) entry stage does NOT create a Review-queue item and
 * no add flow gains an approval step. The backend may reject an add into a
 * terminal stage with `extensions.code === "TERMINAL_ENTRY_NOT_ALLOWED"`; we
 * surface that (and any other error) as a toast and rethrow so the caller can
 * keep the dialog open.
 * @module features/player-kanban/hooks/useAddPlayerToPipeline
 */

import { useCallback, useMemo, useState } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import { useAddPlayerToKanbanMutation } from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

import { MESSAGES } from "../constants";

/** Operation name whose refetch reconciles a mounted board after an add. */
const LIST_KANBAN_CARDS_OP = "ListKanbanCards";

/** Arguments for a single add-to-pipeline call. */
export interface AddPlayerToPipelineArgs {
  /** Target pipeline board id. */
  readonly kanbanBoardId: string;
  /** Target stage (phase) id within that board. */
  readonly kanbanPhaseId: string;
  /** Player being added. */
  readonly playerId: string;
}

/** Public API of {@link useAddPlayerToPipeline}. */
export interface UseAddPlayerToPipelineResult {
  /**
   * Fire the add. Resolves `true` on success (and shows a success toast),
   * rejects on failure (after showing an error toast) so the caller can keep
   * the dialog open for a retry.
   */
  readonly addToPipeline: (args: AddPlayerToPipelineArgs) => Promise<boolean>;
  /** Whether an add is currently in flight. */
  readonly isAdding: boolean;
}

/**
 * Extract the human-facing message from a thrown mutation error, preferring the
 * server's message so `TERMINAL_ENTRY_NOT_ALLOWED` and player-existence copy
 * reach the user verbatim.
 * @param error - The thrown error.
 * @returns A display string, falling back to the generic add-failure copy.
 */
const resolveErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return MESSAGES.ERROR.ADD_PLAYER_FAILED;
};

/**
 * Board-agnostic add-to-pipeline mutation wrapper.
 * @returns The {@link UseAddPlayerToPipelineResult} imperative API.
 */
const useAddPlayerToPipeline = (): UseAddPlayerToPipelineResult => {
  const toast = useToastNotification();
  const [addPlayerToKanban] = useAddPlayerToKanbanMutation();
  const [isAdding, setIsAdding] = useState(false);

  const addToPipeline = useCallback(
    async ({
      kanbanBoardId,
      kanbanPhaseId,
      playerId,
    }: AddPlayerToPipelineArgs): Promise<boolean> => {
      setIsAdding(true);
      try {
        await addPlayerToKanban({
          variables: { input: { kanbanBoardId, kanbanPhaseId, playerId } },
          // Reconcile the board if it is mounted; a no-op otherwise. No
          // optimisticResponse — see the module JSDoc for why.
          refetchQueries: [LIST_KANBAN_CARDS_OP],
        });
        // SE-5508 `card_added`. Inside the `try`, after the awaited mutation
        // resolved — a rejected add falls to the `catch` below and rethrows,
        // so a failed add emits nothing. This mutation carries no hook-level
        // `onError`, so rejection (not an absent-data check) is the failure
        // signal here; contrast `removePlayerFromBoard`, which does have one.
        void AnalyticsTracker.pipeline.logCardAdded({
          source: "add_dialog",
          stage: kanbanPhaseId,
          board_id: kanbanBoardId,
        });
        toast.show({ action: "success", description: "Added to pipeline" });
        return true;
      } catch (error) {
        toast.show({
          action: "danger",
          description: resolveErrorMessage(error),
        });
        throw error instanceof Error ? error : new Error(String(error));
      } finally {
        setIsAdding(false);
      }
    },
    [addPlayerToKanban, toast]
  );

  return useMemo(
    () => ({ addToPipeline, isAdding }),
    [addToPipeline, isAdding]
  );
};

export default useAddPlayerToPipeline;
